import React, { forwardRef, useRef, useState } from 'react'
import styled from 'styled-components'
import ArrowDownIcon from 'src/assets/icons/arrow-down.svg'
import Select from 'react-select'
import { CountryCode, getCountryCallingCode } from 'libphonenumber-js'
import { DEFAULT_FONT_FAMILY } from 'src/utilities/theme'
import { countryData } from 'src/utilities/constants'
import { useTranslation } from 'react-i18next'

export const countryOptions = Object.entries(countryData).map(([code, data]) => ({
  value: code,
  label: `${data.name} (${data.code})`,
  flag: `https://flagcdn.com/w40/${code.toLowerCase()}.png`,
  code: data.code,
}))

const Wrapper = styled.div`
  display: grid;
  row-gap: 0.5rem;
`

const InputContainer = styled.div`
  display: grid;
  grid-template-columns: 4.5rem 1fr 2.75rem;
  height: 3rem;
  border: 1px solid rgba(60, 60, 67, 0.29);
  border-radius: 0.625rem;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.baseItemColor};
`

const Prefix = styled.div`
  display: flex;
  align-items: center;
  font-size: 1rem;
  border-right: 1px solid rgba(60, 60, 67, 0.29);
  background-color: ${({ theme }) => theme.colors.baseItemColor};
  cursor: pointer;
`

const DropdownArrow = styled.img<{ open?: boolean }>`
  width: 1rem;
  height: 1rem;
  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
  margin-top: 1px;
`

const StyledInput = styled.input.attrs({
  autoComplete: 'off',
  name: 'sms-phone-input',
})`
  width: 100%;
  border: none;
  outline: none;
  padding: 0 0.5rem;
  font-size: 1rem;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.baseItemTextColor};

  ::placeholder {
    color: ${({ theme }) => theme.colors.placeholderTextColor};
    opacity: 0.7;
  }
`

const Info = styled.div`
  font-size: 0.625rem;
  color: ${({ theme }) => theme.colors.secondaryTextColor};
`

interface Props {
  value: string
  onChange: (val: string) => void
  defaultCountry?: CountryCode
  info?: string
}

const PhoneNumberInput = forwardRef<HTMLInputElement, Props>(({ onChange, defaultCountry = 'LT', info }, ref) => {
  const [localNumber, setLocalNumber] = useState('')
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(defaultCountry)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [tempHideDialCode, setTempHideDialCode] = useState(false)

  const { t } = useTranslation()

  const selectRef = useRef<any>(null)
  const prefixRef = useRef<HTMLDivElement>(null)

  const dialCode = `+${getCountryCallingCode(selectedCountry)}`

  const formatOptionLabel = (option: any, { context }: any) => {
    if (context === 'value') {
      return tempHideDialCode ? '' : option.code
    }
  
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}>
        <img src={option.flag} alt={option.label} style={{ width: 20 }} />
        <span style={{ whiteSpace: 'nowrap' }}>{option.label}</span>
      </div>
    )
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '')
    setLocalNumber(val)
    onChange(`${dialCode}${val}`)
  }

  return (
    <Wrapper>
      <InputContainer>
      <Prefix ref={prefixRef} onClick={() => selectRef.current?.focus()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Select
              options={countryOptions}
              ref={selectRef}
              onMenuOpen={() => {
                setTempHideDialCode(true)
                setIsDropdownOpen(true)
              }}
              onMenuClose={() => {
                setTempHideDialCode(false)
                setIsDropdownOpen(false)
              }}
              value={countryOptions.find((c) => c.value === selectedCountry)}
              onChange={(opt) => {
                setSelectedCountry(opt!.value as CountryCode)
                onChange(`${opt!.code}${localNumber}`)
              }}
              isSearchable={false}
              menuPortalTarget={document.body}
              formatOptionLabel={formatOptionLabel}
              components={{ IndicatorSeparator: () => null }}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base) => ({
                  ...base,
                  border: 'none',
                  backgroundColor: 'transparent',
                  height: '100%',
                  boxShadow: 'none',
                  minWidth: '3rem',
                }),
                menu: (base) => ({
                  ...base,
                  width: '16rem', 
                }),
                option: (base, state) => ({
                  ...base,
                  padding: '0.75rem 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  backgroundColor: state.isFocused ? '#f0f0f0' : '#fff',
                  color: '#000',
                }),
                singleValue: (base) => ({
                  ...base,
                  fontSize: '1rem',
                  fontFamily: DEFAULT_FONT_FAMILY,
                  color: 'inherit',
                  marginLeft: '0.25rem',
                }),
                valueContainer: (base) => ({
                  ...base,
                  padding: 0,
                  fontFamily: DEFAULT_FONT_FAMILY,
                  justifyContent: 'center',
                }),
                dropdownIndicator: () => ({ display: 'none' }),
                indicatorsContainer: () => ({ display: 'none' }),
              }}
            />
            <DropdownArrow src={ArrowDownIcon} alt="toggle" open={isDropdownOpen} />
          </div>
        </div>
      </Prefix>
        <StyledInput
          type={'number'}
          value={localNumber}
          onChange={handleInputChange}
          placeholder={t('components.phoneNumberModal.input.placeholder')}
          ref={ref}
        />
      </InputContainer>
      {info && <Info>{info}</Info>}
    </Wrapper>
  )
})

export default PhoneNumberInput
